'use client';

import { createContext, useContext, useEffect, useState, ReactNode } from 'react';

interface FirstInteractionContextType {
  hasInteracted: boolean;
}

const FirstInteractionContext = createContext<FirstInteractionContextType>({
  hasInteracted: false,
});

/**
 * Global provider that tracks first user interaction
 * Should be placed high in the component tree (e.g., in layout)
 */
export function FirstInteractionProvider({ children }: { children: ReactNode }) {
  const [hasInteracted, setHasInteracted] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  // Prevent hydration mismatch by only rendering after mount
  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (hasInteracted || !isMounted) return;

    // List of events that indicate user interaction
    const events = ['scroll', 'mousedown', 'touchstart', 'keydown'];
    
    const handleInteraction = () => {
      setHasInteracted(true);
      // Remove listeners after first interaction
      events.forEach(event => {
        window.removeEventListener(event, handleInteraction);
      });
    };

    // Add passive listeners for better scroll performance
    events.forEach(event => {
      window.addEventListener(event, handleInteraction, { 
        passive: true, 
        once: true 
      });
    });

    // Fallback: Load after 3 seconds if no interaction
    const fallbackTimer = setTimeout(() => {
      handleInteraction();
    }, 3000);

    return () => {
      clearTimeout(fallbackTimer);
      events.forEach(event => {
        window.removeEventListener(event, handleInteraction);
      });
    };
  }, [hasInteracted, isMounted]);

  return (
    <FirstInteractionContext.Provider value={{ hasInteracted }}>
      {children}
    </FirstInteractionContext.Provider>
  );
}

/**
 * Hook to check if user has interacted with the page
 * Use this to defer loading heavy components/scripts
 */
export function useFirstInteraction() {
  const context = useContext(FirstInteractionContext);
  if (context === undefined) {
    throw new Error('useFirstInteraction must be used within FirstInteractionProvider');
  }
  return context.hasInteracted;
}